import React, { useState } from 'react';
import {
  CmpMemberDashboardTestContactTypeEnum as ContactType,
  CmpMemberDashboardTestModeReturn,
  CmpMemberDashboardTestPlanEnum as Plan,
  CmpMemberDashboardTestSwitchChamberEnum as SwitchChamber,
  useSetMyCmpMemberDashboardTestModeMutation,
} from '../../../graphql/types';

// Monday 13301616787 (Issue Fixer 9). Not in design (Moeen approved Oct 16
// 2026: top of the left nav, Sand Bay only). The backend sets
// testMode.available only for the Sand Bay sandbox chamber, so this renders
// nothing anywhere else, and nothing before getUser answers (no flash).
// The test view can only narrow access; the backend enforces it.

const GOLD = '#E1A731';
const GRAY = '#727272';
const BLACK = '#000000';

const PLAN_OPTIONS: [Plan, string][] = [
  [Plan.Accelerator, 'Accelerator'], [Plan.Essentials, 'Essentials'],
  [Plan.CpaStandard, 'CPA Standard'], [Plan.CpaBasic, 'CPA Basic'],
];
// Design viewingAs names (CMP Accelerator Member Dashboard (1)) plus HR.
const CONTACT_OPTIONS: [ContactType, string][] = [
  [ContactType.PrimaryContact, 'Primary Contact'], [ContactType.AccountsPayable, 'Accounts Payable'],
  [ContactType.SalesPerson, 'Sales Person'], [ContactType.Marketing, 'Marketing'],
  [ContactType.Administrative, 'Administrative'], [ContactType.Hr, 'HR'],
];
const SWITCH_OPTIONS: [SwitchChamber, string][] = [[SwitchChamber.Show, 'Show'], [SwitchChamber.Hide, 'Hide']];

type Values = { plan: Plan | null; contactType: ContactType | null; switchChamber: SwitchChamber | null };

const selectStyle: React.CSSProperties = {
  flex: 1, minWidth: 0, fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: BLACK, background: '#FFFFFF',
  border: '1px solid #D8D8D8', borderRadius: 6, padding: '4px 6px', height: 28, boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = { width: 62, flexShrink: 0, fontSize: 11, fontWeight: 600, color: GRAY, lineHeight: 1.2 };

/** Renders nothing (and runs no hook) unless the backend says test mode is available. */
export default function CmpMemberTestMode({ testMode }: { testMode?: CmpMemberDashboardTestModeReturn | null }) {
  return testMode && testMode.available === true ? <TestModeControls testMode={testMode} /> : null;
}

function TestModeControls({ testMode }: { testMode: CmpMemberDashboardTestModeReturn }) {
  // Reload access everywhere getUser is shown (sidebar, routes, pages).
  const [save, { loading }] = useSetMyCmpMemberDashboardTestModeMutation({ refetchQueries: ['getUser'], awaitRefetchQueries: true });
  const [error, setError] = useState('');

  const current: Values = { plan: testMode.plan || null, contactType: testMode.contactType || null, switchChamber: testMode.switchChamber || null };
  const apply = async (next: Values) => {
    setError('');
    try {
      await save({ variables: { input: next } });
    } catch {
      setError('Test mode was not saved. Refresh the page and try again.');
    }
  };
  const row = <T extends string>(label: string, testId: string, value: T | null, options: [T, string][], realLabel: string, key: keyof Values) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={labelStyle}>{label}</span>
      <select data-testid={testId} aria-label={`Test mode ${label}`} value={value || ''} disabled={loading} style={selectStyle}
        onChange={event => apply({ ...current, [key]: event.target.value || null })}>
        <option value="">{realLabel}</option>
        {options.map(([optionValue, text]) => <option key={optionValue} value={optionValue}>{text}</option>)}
      </select>
    </label>
  );
  const active = !!(current.plan || current.contactType || current.switchChamber);

  return (
    <div data-testid="cmp-member-test-mode" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 10px 12px', margin: '0 0 14px',
      border: `1px dashed ${GOLD}`, borderRadius: 10, background: '#FFFBF2', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: GOLD, textTransform: 'uppercase' }}>Test mode</span>
        <button type="button" data-testid="cmp-member-test-mode-reset" disabled={loading || !active} onClick={() => apply({ plan: null, contactType: null, switchChamber: null })}
          style={{ background: 'transparent', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
            color: active ? BLACK : GRAY, textDecoration: active ? 'underline' : 'none', cursor: loading || !active ? 'default' : 'pointer' }}>
          Reset
        </button>
      </div>
      {row('Plan', 'cmp-member-test-plan', current.plan, PLAN_OPTIONS, 'Actual plan', 'plan')}
      {row('View as', 'cmp-member-test-contact', current.contactType, CONTACT_OPTIONS, 'Actual contact', 'contactType')}
      {row('Switch Chamber', 'cmp-member-test-switch', current.switchChamber, SWITCH_OPTIONS, 'Actual', 'switchChamber')}
      {error ? <div role="alert" style={{ fontSize: 11, color: '#B91C1C', lineHeight: 1.4 }}>{error}</div> : null}
    </div>
  );
}
